/**
 * Tier helpers — the single mathematical + visual source of truth for
 * engagement tier assignment. Colors, ranges, and glossary ids live in
 * `glossary.ts`; this file re-exports them in a component-friendly shape
 * plus a `tierFromScore()` function used everywhere a score needs to
 * become a visual pill.
 *
 * IMPORTANT: the threshold boundaries here MUST stay in sync with the
 * backend's tier-assignment logic. Changing them changes every pill +
 * every heatmap color in the dashboard.
 */
import { TIER_PALETTE } from './glossary';

export type TierKey = 'anchor' | 'hot' | 'warm' | 'cool';

export type TierMeta = {
  key: TierKey;
  label: string;
  /** Raw hex, for inline style (svg fills, border-left stripes, etc.) */
  hex: string;
  /** Tailwind class bundles — use these in JSX so the palette stays themed. */
  bgClass: string;
  textClass: string;
  borderClass: string;
  /** Inclusive lower / upper score bound. */
  range: [number, number];
  /** Cross-reference into glossary.ts so InfoTips resolve cleanly. */
  glossaryId: string;
};

export const TIER_META: Record<TierKey, TierMeta> = {
  anchor: {
    key: 'anchor',
    label: 'Anchor',
    hex: TIER_PALETTE.anchor.hex,
    bgClass: 'bg-yellow-500/15',
    textClass: 'text-yellow-300',
    borderClass: 'border-yellow-500/40',
    range: [75, 100],
    glossaryId: 'anchor_tier',
  },
  hot: {
    key: 'hot',
    label: 'Hot',
    hex: TIER_PALETTE.hot.hex,
    bgClass: 'bg-red-600/20',
    textClass: 'text-red-300',
    borderClass: 'border-red-600/40',
    range: [60, 74],
    glossaryId: 'hot_tier',
  },
  warm: {
    key: 'warm',
    label: 'Warm',
    hex: TIER_PALETTE.warm.hex,
    bgClass: 'bg-orange-500/15',
    textClass: 'text-orange-300',
    borderClass: 'border-orange-500/40',
    range: [40, 59],
    glossaryId: 'warm_tier',
  },
  cool: {
    key: 'cool',
    label: 'Cool',
    hex: TIER_PALETTE.cool.hex,
    bgClass: 'bg-blue-600/20',
    textClass: 'text-blue-300',
    borderClass: 'border-blue-600/40',
    range: [0, 39],
    glossaryId: 'cool_tier',
  },
};

export const TIER_ORDER: TierKey[] = ['anchor', 'hot', 'warm', 'cool'];

/**
 * Map a raw 0 – 100 score to its tier metadata. NaN / null / undefined
 * all fall through to the Cool tier so callers never have to guard.
 */
export function tierFromScore(score: number | null | undefined): TierMeta {
  if (score == null || Number.isNaN(score)) return TIER_META.cool;
  if (score >= 75) return TIER_META.anchor;
  if (score >= 60) return TIER_META.hot;
  if (score >= 40) return TIER_META.warm;
  return TIER_META.cool;
}

/**
 * Convenience: return a tier by its uppercase backend key
 * (SUPER_VOTER / HOT / WARM / COOL). Anything unknown falls through to Cool.
 */
export function tierFromBackendKey(key: string | null | undefined): TierMeta {
  switch ((key ?? '').toUpperCase()) {
    case 'SUPER_VOTER':
    case 'ANCHOR':
      return TIER_META.anchor;
    case 'HOT':
      return TIER_META.hot;
    case 'WARM':
      return TIER_META.warm;
    default:
      return TIER_META.cool;
  }
}
